import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Mail,
  Smartphone,
  Bell,
  Settings,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Send,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import api from '../lib/api';
import { isPushSupported, getPushSubscription, subscribeToPush, unsubscribeFromPush } from '../lib/push';
import { BellRing } from 'lucide-react';

export default function NotificationSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [prefs, setPrefs] = useState({
    email_enabled: true,
    sms_enabled: true,
  });
  const [serviceStatus, setServiceStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pushState, setPushState] = useState('checking'); // checking | unsupported | subscribed | not-subscribed
  const [pushBusy, setPushBusy] = useState(false);
  const [testingPush, setTestingPush] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [prefsRes, statusRes] = await Promise.all([
        notificationsAPI.getPrefs(),
        notificationsAPI.getServiceStatus(),
      ]);
      setPrefs(prefsRes.data);
      setServiceStatus(statusRes.data);

      if (isAdmin) {
        const logsRes = await notificationsAPI.getLogs();
        setLogs(logsRes.data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
    const checkPush = async () => {
      if (!isPushSupported()) {
        setPushState('unsupported');
        return;
      }
      try {
        const sub = await getPushSubscription();
        setPushState(sub ? 'subscribed' : 'not-subscribed');
      } catch (e) {
        setPushState('not-subscribed');
      }
    };
    checkPush();
  }, [fetchData]);

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await subscribeToPush();
      setPushState('subscribed');
      toast.success('Push notifications enabled on this device');
    } catch (error) {
      toast.error(error.message || 'Failed to enable push notifications');
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      setPushState('not-subscribed');
      toast.success('Push notifications disabled on this device');
    } catch (error) {
      toast.error('Failed to disable push notifications');
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      await api.post('/push/test');
      toast.success('Test push sent — check your notifications');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test push');
    } finally {
      setTestingPush(false);
    }
  };

  const handleToggle = async (field) => {
    const newPrefs = { ...prefs, [field]: !prefs[field] };
    setPrefs(newPrefs);
    setSaving(true);

    try {
      await notificationsAPI.updatePrefs(newPrefs);
      toast.success('Preferences updated');
    } catch (error) {
      setPrefs(prefs); // Revert on error
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSms = async () => {
    if (!user?.phone) {
      toast.error('No phone number on your profile. Please update your profile with a phone number first.');
      return;
    }
    
    setTestingSms(true);
    try {
      await api.post('/notifications/test-sms');
      toast.success(`Test SMS sent to ${user.phone}`);
      // Refresh logs to show the test message
      if (isAdmin) {
        const logsRes = await notificationsAPI.getLogs();
        setLogs(logsRes.data.logs || []);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send test SMS';
      toast.error(errorMsg);
    } finally {
      setTestingSms(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await api.post('/notifications/test-email');
      toast.success(`Test email sent to ${user.email}`);
      // Refresh logs to show the test message
      if (isAdmin) {
        const logsRes = await notificationsAPI.getLogs();
        setLogs(logsRes.data.logs || []);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send test email';
      toast.error(errorMsg);
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-3xl">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="notification-settings">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold">Notification Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage how you receive job assignment and update notifications
        </p>
      </div>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notification Channels
          </CardTitle>
          <CardDescription>
            Choose how you want to be notified about job assignments and updates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <Label className="text-base font-medium">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications via email (Resend)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {serviceStatus?.email?.mocked && (
                <Badge variant="secondary" className="text-xs">DEMO</Badge>
              )}
              <Switch
                checked={prefs.email_enabled}
                onCheckedChange={() => handleToggle('email_enabled')}
                disabled={saving}
                data-testid="email-toggle"
              />
            </div>
          </div>

          {/* SMS Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <Label className="text-base font-medium">SMS Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications via SMS (Vonage)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {serviceStatus?.sms?.mocked && (
                <Badge variant="secondary" className="text-xs">DEMO</Badge>
              )}
              <Switch
                checked={prefs.sms_enabled}
                onCheckedChange={() => handleToggle('sms_enabled')}
                disabled={saving}
                data-testid="sms-toggle"
              />
            </div>
          </div>

          {/* Info about demo mode */}
          {(serviceStatus?.email?.mocked || serviceStatus?.sms?.mocked) && (
            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Demo Mode:</strong> Email and SMS notifications are currently simulated. 
                To enable real notifications, configure the Resend API key for email and 
                Vonage credentials for SMS in the environment variables.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card data-testid="push-notifications-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <BellRing className="w-5 h-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Get lock-screen alerts on this device for job assignments, timesheet updates, and time off decisions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {pushState === 'unsupported' ? (
            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Push notifications aren't available in this browser.{' '}
                <strong>On iPhone/iPad:</strong> open this site in Safari, tap Share → "Add to Home Screen",
                then open the app from your home screen and return here to enable push.
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                  <BellRing className="w-5 h-5" />
                </div>
                <div>
                  <Label className="text-base font-medium">This Device</Label>
                  <p className="text-sm text-muted-foreground">
                    {pushState === 'subscribed'
                      ? 'Push notifications are enabled on this device'
                      : 'Enable push to get alerts even when the app is closed'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {pushState === 'subscribed' ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestPush}
                      disabled={testingPush}
                      data-testid="test-push-btn"
                    >
                      {testingPush ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDisablePush}
                      disabled={pushBusy}
                      data-testid="disable-push-btn"
                    >
                      Disable
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleEnablePush}
                    disabled={pushBusy || pushState === 'checking'}
                    data-testid="enable-push-btn"
                  >
                    {pushBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BellRing className="w-4 h-4 mr-2" />}
                    Enable Push
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Service Status (Admin only) */}
      {isAdmin && serviceStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Service Status
            </CardTitle>
            <CardDescription>
              Current status of notification services
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Email ({serviceStatus.email?.provider || 'Not Configured'})</span>
                  {serviceStatus.email?.enabled ? (
                    <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Not Configured</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {serviceStatus.email?.enabled 
                    ? 'Connected and ready to send emails'
                    : serviceStatus.email?.note || 'Configure RESEND_API_KEY or SMTP credentials in .env'}
                </p>
                {serviceStatus.email?.enabled && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestEmail}
                    disabled={testingEmail}
                    className="mt-1"
                    data-testid="test-email-btn"
                  >
                    {testingEmail ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {testingEmail ? 'Sending...' : 'Send Test Email'}
                  </Button>
                )}
              </div>

              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">SMS ({serviceStatus.sms?.provider || 'Not Configured'})</span>
                  {serviceStatus.sms?.enabled ? (
                    <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Not Configured</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {serviceStatus.sms?.enabled 
                    ? `Connected via ${serviceStatus.sms?.phone_number || 'Vonage'}`
                    : serviceStatus.sms?.note || 'Configure Vonage credentials in .env'}
                </p>
                {serviceStatus.sms?.enabled && serviceStatus.sms?.note && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ {serviceStatus.sms.note}
                  </p>
                )}
                {serviceStatus.sms?.enabled && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestSms}
                    disabled={testingSms}
                    className="mt-3"
                    data-testid="test-sms-btn"
                  >
                    {testingSms ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {testingSms ? 'Sending...' : 'Send Test SMS'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification Logs (Admin only) */}
      {isAdmin && logs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              Recent Notification Logs
            </CardTitle>
            <CardDescription>
              History of sent (or simulated) notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                  data-testid={`notification-log-${log.id}`}
                >
                  <div className={`p-2 rounded-full ${
                    log.type === 'email' 
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' 
                      : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                  }`}>
                    {log.type === 'email' ? <Mail className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{log.subject}</span>
                      {log.mocked && (
                        <Badge variant="secondary" className="text-xs">DEMO</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      To: {log.recipient}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(parseISO(log.timestamp), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
